// src/components/comments/NotifyModal.jsx
//
// "Notify" picker for a comment. Enforces the ticket's rule that a given person
// can only be notified about a given comment ONCE — already-notified people are
// listed but disabled, so the restriction is visible rather than a silent
// failure when you try.
//
// The rule is enforced server-side as well (comments.py notify_about_comment
// returns 409). This UI is the courtesy layer, not the guarantee: two authors
// with the panel open at once still can't double-notify.
import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Checkbox, Avatar, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const GOLD = '#D4AF37';
const INK = '#1A1A1A';

const idOf = (u) => String(u?._id || u?.id || '');
const nameOf = (u) =>
  u?.username || u?.name || (u?.email ? String(u.email).split('@')[0] : 'User');
const initialsOf = (u) => {
  const parts = String(nameOf(u)).trim().split(/\s+/);
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] || '');
  return (a + b).toUpperCase();
};

export default function NotifyModal({
  open,
  comment,
  candidates = [],
  onClose,
  onSubmit,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Anyone already notified about THIS comment is off-limits.
  const alreadyNotified = useMemo(() => {
    const list = comment?.notified_user_ids || [];
    return new Set(list.map(String));
  }, [comment]);

  // Reset whenever the dialog opens against a different comment, so a previous
  // selection can't leak into the next notify.
  useEffect(() => {
    if (open) { setSelected(new Set()); setError(''); setSending(false); }
  }, [open, comment?.id]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const handleSubmit = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSending(true);
    setError('');
    try {
      await onSubmit?.(ids);
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Could not send the notification.');
    } finally {
      setSending(false);
    }
  };

  const selectable = candidates.filter((u) => !alreadyNotified.has(idOf(u)));

  return (
    <Dialog open={!!open} onClose={() => !sending && onClose?.()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Notify about this comment</Typography>
        <IconButton size="small" onClick={onClose} disabled={sending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {candidates.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
            There's nobody else on this draft yet. Invite a collaborator first.
          </Typography>
        )}

        {candidates.map((u) => {
          const uid = idOf(u);
          const done = alreadyNotified.has(uid);
          const checked = selected.has(uid);
          return (
            <Box
              key={uid}
              onClick={() => !done && toggle(uid)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 0.5, py: 0.75,
                borderRadius: '6px',
                cursor: done ? 'default' : 'pointer',
                opacity: done ? 0.55 : 1,
                '&:hover': { backgroundColor: done ? 'transparent' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <Checkbox
                checked={checked}
                disabled={done}
                size="small"
                sx={{ p: 0.5, '&.Mui-checked': { color: GOLD } }}
              />
              <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: '#6B4FA0' }}>
                {initialsOf(u)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK }}>
                  {nameOf(u)}
                </Typography>
                {done && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <CheckCircleIcon sx={{ fontSize: 12 }} /> Already notified
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        {candidates.length > 0 && selectable.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', pt: 1.5 }}>
            Everyone on this draft has already been notified about this comment.
          </Typography>
        )}

        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', pt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={sending} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={sending || selected.size === 0}
          startIcon={sending ? <CircularProgress size={14} sx={{ color: INK }} /> : null}
          sx={{ bgcolor: GOLD, color: INK, fontWeight: 700, borderRadius: '8px', minWidth: 120, '&:hover': { bgcolor: '#F5D76E' } }}
        >
          {sending ? 'Sending…' : `Notify${selected.size ? ` (${selected.size})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
